import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Settings, RefreshCw, Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useNotifications } from '../../contexts/NotificationsContext';
import Avatar from '../ui/Avatar';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { ROUTES } from '../../config/constants';

const SUPPORT_EMAIL = 'costa.nicolas@ctrl-ia.com.ar';

const TopBar = () => {
  const { profile, signOut } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { unregisterCurrentDevice } = useNotifications();
  const [menuOpen, setMenuOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const handleRefresh = () => {
    // Hard reload: bypass cache. En PWA equivale a tirar hacia abajo en mobile.
    window.location.reload();
  };

  const handleSignOut = async () => {
    try {
      setSigningOut(true);
      // Desregistramos el token FCM de ESTE browser antes de cerrar sesión.
      // Si esta misma máquina la comparte otro miembro del hogar, evitamos
      // que reciba push dirigidos al user anterior. Otros devices del mismo
      // usuario siguen recibiendo (tokens son por device).
      await unregisterCurrentDevice();
      await signOut();
      navigate(ROUTES.LOGIN, { replace: true });
    } catch (err) {
      toast.error('No se pudo cerrar sesión. Reintentá.');
    } finally {
      setSigningOut(false);
      setMenuOpen(false);
    }
  };

  const handleOpenSettings = () => {
    setMenuOpen(false);
    navigate(ROUTES.SETTINGS);
  };

  return (
    <>
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/80 backdrop-blur-md pt-safe">
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-brand-700">PetCare</span>
            <span className="text-lg font-light text-slate-400">Family</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setHelpOpen(true)}
              className="rounded-full px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-800 transition-colors"
            >
              Ayuda
            </button>
            <button
              onClick={() => setMenuOpen(true)}
              aria-label="Abrir menú de usuario"
              className="rounded-full transition hover:opacity-80"
            >
              <Avatar src={profile?.photoURL} name={profile?.displayName} size="sm" />
            </button>
          </div>
        </div>
      </header>

      <Modal isOpen={menuOpen} onClose={() => setMenuOpen(false)} title="Tu cuenta">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <Avatar src={profile?.photoURL} name={profile?.displayName} size="lg" />
            <div className="min-w-0">
              <p className="truncate font-medium text-slate-900">
                {profile?.displayName || 'Usuario'}
              </p>
              <p className="truncate text-sm text-slate-500">{profile?.email}</p>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Button
              variant="secondary"
              leftIcon={Settings}
              fullWidth
              onClick={handleOpenSettings}
            >
              Configuración
            </Button>
            <Button
              variant="danger"
              leftIcon={LogOut}
              fullWidth
              isLoading={signingOut}
              onClick={handleSignOut}
            >
              Cerrar sesión
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={helpOpen} onClose={() => setHelpOpen(false)} title="Ayuda">
        <div className="flex flex-col gap-4">
          <div className="rounded-xl bg-brand-50 px-4 py-3 ring-1 ring-brand-100">
            <div className="flex items-start gap-3">
              <RefreshCw
                className="h-5 w-5 shrink-0 text-brand-700 mt-0.5"
                aria-hidden="true"
              />
              <div>
                <p className="text-sm font-semibold text-brand-900">
                  ¿Algo no se ve bien?
                </p>
                <p className="mt-1 text-sm text-brand-800">
                  A veces la app necesita refrescarse para mostrar cambios
                  recientes (un balde nuevo, una mascota agregada, etc.).
                  Refrescá la pantalla y volvé a entrar.
                </p>
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              fullWidth
              leftIcon={RefreshCw}
              onClick={handleRefresh}
              className="mt-3"
            >
              Refrescar la app
            </Button>
          </div>

          <div className="rounded-xl bg-slate-50 px-4 py-3 ring-1 ring-slate-100">
            <div className="flex items-start gap-3">
              <Mail
                className="h-5 w-5 shrink-0 text-slate-600 mt-0.5"
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900">
                  ¿Sigue sin funcionar?
                </p>
                <p className="mt-1 text-sm text-slate-700">
                  Escribime contándome qué pasó y, si podés, una captura de
                  pantalla. Te respondo lo antes posible.
                </p>
                <a
                  href={`mailto:${SUPPORT_EMAIL}?subject=PetCare%20Family%20-%20Ayuda`}
                  className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:text-brand-800 break-all"
                >
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  {SUPPORT_EMAIL}
                </a>
              </div>
            </div>
          </div>

          <Button
            variant="secondary"
            fullWidth
            onClick={() => setHelpOpen(false)}
          >
            Cerrar
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default TopBar;